import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { MediaTile } from "@/components/MediaTile";
import { MediaLightbox } from "@/components/MediaLightbox";
import { api, fileUrl, thumbUrl, resolveAvatar } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MapPin, Loader2, Star } from "lucide-react";

const TABS = [
  { id: "latest", label: "Latest", sort: "recent" },
  { id: "trending", label: "Trending", sort: "trending" },
  { id: "top", label: "Top Rated", sort: "top" },
  { id: "photos", label: "Photos", sort: "recent", type: "image" },
  { id: "videos", label: "Videos", sort: "recent", type: "video" },
  { id: "uploaders", label: "Uploaders" },
];

const LocationPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("latest");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const t = TABS.find((x) => x.id === tab);
    try {
      const params = { sort: t?.sort || "recent", limit: 500 }; // "View all" page: no preview cap
      if (t?.type) params.type = t.type;
      const r = await api.get(`/location/${slug}`, { params });
      setData(r.data);
    } catch { setData(null); } finally { setLoading(false); }
  }, [slug, tab]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="min-h-screen relative z-10"><Navbar /><div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></div>;
  if (!data) return <div className="min-h-screen relative z-10"><Navbar /><div className="text-center py-40 text-muted-foreground">No uploads for this location yet.</div></div>;

  return (
    <div className="min-h-screen relative z-10">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/map")} className="text-sm text-primary hover:underline mb-3">← Back to map</button>
        <div className="flex items-center gap-2 text-primary"><MapPin className="w-5 h-5" /><span className="text-xs tracking-[0.2em] uppercase font-bold">Location</span></div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter mt-1">{data.city}<span className="text-muted-foreground">, {data.country}</span></h1>
        <p className="text-muted-foreground mt-2">{data.total_unique_media ?? data.upload_count} photos &amp; videos · {data.uploader_count} uploaders</p>

        <div className="flex flex-wrap gap-1 mt-6 border-b border-border">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`loc-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={cn("px-4 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2", tab === t.id ? "text-primary border-primary" : "text-muted-foreground border-transparent hover:text-foreground")}>{t.label}</button>
          ))}
        </div>

        <div className="mt-6">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
            : tab === "uploaders" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.uploaders.map((u) => (
                  <div key={u.user_id} data-testid={`loc-uploader-${u.user_id}`} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-12 h-12 border border-border"><AvatarImage src={resolveAvatar(u.avatar_url)} /><AvatarFallback className="bg-secondary">{u.name?.[0]}</AvatarFallback></Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">@{u.name}</div>
                        <div className="text-xs text-muted-foreground">{data.city} · {u.count} uploads · ★ {u.avg_rating || "—"}</div>
                      </div>
                    </div>
                    <div className="flex gap-1.5 mt-3">
                      {u.thumbs.map((t) => <div key={t.id} className="w-1/3 aspect-square rounded-lg overflow-hidden bg-black">{t.media_type === "video" ? (t.cloud_poster_url ? <img src={t.cloud_poster_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-secondary/40" />) : <img src={thumbUrl(t.file_ref, 200)} alt="" className="w-full h-full object-cover" />}</div>)}
                    </div>
                    <Button data-testid={`loc-uploader-profile-${u.user_id}`} onClick={() => navigate(`/u/${u.user_id}`)} variant="outline" className="w-full mt-3 rounded-full border-border">View Profile</Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {data.uploads.length === 0 ? (
                  <div className="col-span-full border border-dashed border-border rounded-xl py-16 text-center text-muted-foreground" data-testid="loc-empty">No {tab} here yet — try another tab.</div>
                ) : data.uploads.map((m, i) => <MediaTile key={m.id} item={m} index={i} onOpen={(x) => setSelected(x.id)} />)}
              </div>
            )}
        </div>
      </div>
      <MediaLightbox mediaId={selected} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default LocationPage;
